/*
 * thrux-pricing-model.js — pure THRUX pack-pricing + selection model.
 *
 * ONE source of truth for the picker math, shared by the browser (loaded as a
 * deferred <script>, exposes window.ThruxPricing) and Node (require()), so the
 * unit tests exercise exactly the code the theme ships.
 *
 * NO DOM, NO Shopify globals, NO typed prices. Every figure comes from the shop
 * metafield `thrux.pack_pricing` (the same one the Product Discount Function
 * reads — see subscription-picker/2026-09-09). The theme renders the EXPLICIT
 * per-tier cents from that config verbatim; nothing here recomputes the 0.8
 * factor (kept only for the cross-check test), so display == the Function charge.
 *
 * Config shape (C2 ruling, STATE 2026-09-09 06:38 PT):
 *   {
 *     version, currency,
 *     base_variant_cents,                       // 1-can price (also the Shopify variant price)
 *     variant_ids: [<cider>,<redline>,<neon>],  // strings; the Function's allocation targets
 *     subscribe_factor,                          // informational (0.8); NOT recomputed here
 *     tiers: [ {cans, onetime_cents, subscribe_cents}, ... ],  // 1/3/6/10
 *     selling_plan_group_ids: [<test>, <live>],  // theme renders the FIRST present on the product
 *     retired_plan_ids: ["6632046835"]           // never rendered as a purchase option
 *   }
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.ThruxPricing = factory();
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  // Pack steps largest-first — MUST match the Product Discount Function's greedy
  // decomposition, so an off-picker cart quantity displays the same total it charges.
  // The Function decomposes over EVERY config tier (sorted largest-first), so
  // quantityTotalCents derives its steps from the config (stepsFor); this list is only
  // the default for decompose() without a config. 2026-09-23: the 15-can at $99.99 keeps
  // greedy == the cheapest pack combination for every cart 1..1000 (at $109.99 it would
  // overcharge 20/23/35/38... cans, which is why the 15 is $99.99).
  var PACK_STEPS = [15, 10, 6, 3, 1];

  function num(v) { return (typeof v === 'number' && isFinite(v)) ? v : null; }

  // Normalize a selling-plan id to its trailing id segment so comparisons work
  // whether the id arrives bare-numeric (6632046835), as a numeric string, or as
  // a Shopify GID ("gid://shopify/SellingPlan/6632046835"). The storefront/Liquid
  // exposes bare-numeric plan ids while the Admin/Function config space uses GIDs.
  // Group ids can't be matched cross-side at all (Liquid exposes an opaque hash,
  // not the Admin GID), so the config identifies the subscription plans by a
  // numeric-string ALLOW-LIST of plan ids (config field selling_plan_ids, C2/tsr1
  // ruling 2026-09-09 07:22 PT) -- plan ids DO match on both sides. The theme
  // renders exactly the allowed, non-retired plans as cadences.
  function normalizePlanId(id) {
    return String(id == null ? '' : id).split('/').pop().split('?')[0];
  }

  // Normalize the raw metafield value (object or JSON string) to a stable shape,
  // or null when absent/malformed so the caller renders "tier unavailable" (never
  // a fallback price table in code).
  function parseConfig(raw) {
    var cfg = raw;
    if (typeof raw === 'string') {
      if (!raw.trim() || raw.trim() === 'null') return null;
      try { cfg = JSON.parse(raw); } catch (e) { return null; }
    }
    if (!cfg || typeof cfg !== 'object' || Array.isArray(cfg)) return null;
    if (!Array.isArray(cfg.tiers) || !cfg.tiers.length) return null;
    var tiers = {};
    for (var i = 0; i < cfg.tiers.length; i++) {
      var t = cfg.tiers[i];
      if (!t || typeof t.cans !== 'number') continue;
      var one = num(t.onetime_cents);
      if (one == null) continue; // one-time is required for a tier to be usable
      // onetime_only tiers (the 15, 2026-09-23) carry a guard subscribe_cents for the deployed checkout
      // Function only; the picker must treat them as having no subscribe price.
      var sub = t.onetime_only === true ? null : num(t.subscribe_cents);
      // config v2 (C2 2026-09-09): subscription is one fixed-unit plan per (pack size x interval).
      // tiers[].plans maps interval-months (string "1"/"2"/"3") -> plan id (numeric string). The
      // Function no longer discounts subscription lines, so the plan sets the price and the display
      // subscribe total is tiers[].subscribe_cents (== plan unit x cans). Normalize plan ids so they
      // match the storefront's bare-numeric plan ids.
      var plans = {};
      if (t.plans && typeof t.plans === 'object') {
        for (var k in t.plans) {
          if (t.plans.hasOwnProperty(k) && t.plans[k] != null) plans[String(k)] = normalizePlanId(t.plans[k]);
        }
      }
      tiers[t.cans] = { cans: t.cans, onetimeCents: one, subscribeCents: (sub == null ? null : sub), plans: plans };
    }
    if (!Object.keys(tiers).length) return null;
    return {
      version: (cfg.version != null ? cfg.version : null),
      currency: cfg.currency || 'USD',
      baseVariantCents: num(cfg.base_variant_cents),
      variantIds: Array.isArray(cfg.variant_ids) ? cfg.variant_ids.map(String) : [],
      subscribeFactor: (typeof cfg.subscribe_factor === 'number' ? cfg.subscribe_factor : 0.8),
      tiers: tiers,
      sellingPlanIds: Array.isArray(cfg.selling_plan_ids) ? cfg.selling_plan_ids.map(normalizePlanId) : [],
      retiredPlanIds: Array.isArray(cfg.retired_plan_ids) ? cfg.retired_plan_ids.map(normalizePlanId) : []
    };
  }

  function tier(cfg, cans) {
    if (!cfg || !cfg.tiers) return null;
    return cfg.tiers[cans] || null;
  }

  // Total cents for a canonical tier at a purchase type ('onetime' | 'subscribe').
  // null when the tier or (for subscribe) its subscribe price is absent.
  function tierTotalCents(cfg, cans, type) {
    var t = tier(cfg, cans);
    if (!t) return null;
    if (type === 'subscribe') return (t.subscribeCents != null ? t.subscribeCents : null);
    return t.onetimeCents;
  }

  // Whole-cent cost of one pouch (20 pouches per can) — the "X cents a pouch" aside.
  function perPouchCents(totalCents, cans) {
    if (totalCents == null || !cans) return null;
    return Math.round(totalCents / (cans * 20));
  }

  // Subscribe savings per delivery (one-time total minus subscribe total).
  function savingsCents(cfg, cans) {
    var t = tier(cfg, cans);
    if (!t || t.subscribeCents == null) return null;
    return t.onetimeCents - t.subscribeCents;
  }

  // The config's tier sizes, largest-first (what the Function decomposes over).
  function stepsFor(cfg) {
    if (!cfg || !cfg.tiers) return PACK_STEPS;
    var s = Object.keys(cfg.tiers).map(Number).filter(function (n) { return n > 0; });
    s.sort(function (a, b) { return b - a; });
    return s.length ? s : PACK_STEPS;
  }

  // Greedy decomposition into `steps` (default PACK_STEPS). e.g. 7 -> [6,1], 5 -> [3,1,1].
  function decompose(cans, steps) {
    steps = steps || PACK_STEPS;
    var out = [], rem = cans;
    for (var i = 0; i < steps.length; i++) {
      while (rem >= steps[i]) { out.push(steps[i]); rem -= steps[i]; }
    }
    return out;
  }

  // Total cents for an arbitrary quantity (cart edits outside the picker), summing
  // the decomposition's tier targets. null if any needed step tier is absent.
  function quantityTotalCents(cfg, cans, type) {
    var steps = decompose(cans, stepsFor(cfg)), total = 0;
    for (var i = 0; i < steps.length; i++) {
      var c = tierTotalCents(cfg, steps[i], type);
      if (c == null) return null;
      total += c;
    }
    return total;
  }

  // Default flavor split for a pack size, front-loading the remainder in flavor
  // order: 1 -> [1,0,0], 3 -> 1/1/1, 6 -> 2/2/2, 10 -> 4/3/3.
  function presetMix(cans, flavors) {
    flavors = flavors || ['cider', 'redline', 'neon'];
    var counts = {};
    flavors.forEach(function (f) { counts[f] = 0; });
    if (cans <= 0) return counts;
    if (cans === 1) { counts[flavors[0]] = 1; return counts; }
    var base = Math.floor(cans / flavors.length), extra = cans % flavors.length;
    flavors.forEach(function (f, i) { counts[f] = base + (i < extra ? 1 : 0); });
    return counts;
  }

  // Exact-quantity validation: the mix must select exactly `cans` cans.
  function validateMix(counts, cans) {
    var selected = 0;
    for (var k in counts) if (counts.hasOwnProperty(k)) selected += (counts[k] || 0);
    return { valid: selected === cans, selected: selected, needed: cans };
  }

  // Build the /cart/add items[] payload from a flavor->variantId map and a
  // flavor->count mix. Adds selling_plan to EVERY line when subscribing; omits it
  // (one-time) otherwise. Zero-count flavors drop out.
  function lineItems(flavorToVariantId, mix, sellingPlanId) {
    var items = [];
    for (var f in mix) {
      if (!mix.hasOwnProperty(f)) continue;
      var q = mix[f] || 0;
      if (q <= 0) continue;
      var id = flavorToVariantId[f];
      if (!id) continue;
      var item = { id: String(id), quantity: q };
      if (sellingPlanId) item.selling_plan = sellingPlanId;
      items.push(item);
    }
    return items;
  }

  // Is this plan one the picker may offer? True only when it is in the config's
  // selling_plan_ids allow-list AND not in retired_plan_ids. Empty allow-list ->
  // subscribe unavailable (one-time only). Robust to bare/numeric/GID id forms.
  function isAllowedPlan(cfg, planId) {
    if (!cfg || !cfg.sellingPlanIds.length) return false;
    var id = normalizePlanId(planId);
    return cfg.sellingPlanIds.indexOf(id) !== -1 && cfg.retiredPlanIds.indexOf(id) === -1;
  }

  // The plan ids the picker should render as cadences: the product's actual plan
  // ids intersected with the allow-list, minus retired. Preserves the product's
  // plan order (the caller sorts monthly-first). Normalized to bare-numeric.
  function allowedPlans(cfg, availablePlanIds) {
    if (!cfg || !cfg.sellingPlanIds.length) return [];
    var seen = {};
    return (availablePlanIds || []).map(normalizePlanId).filter(function (id) {
      if (seen[id]) return false;
      seen[id] = true;
      return cfg.sellingPlanIds.indexOf(id) !== -1 && cfg.retiredPlanIds.indexOf(id) === -1;
    });
  }

  // config v2: the plan id for a (pack size, interval-months) pair. selection = tiers[cans].plans[interval].
  function planFor(cfg, cans, interval) {
    if (!cfg || !cfg.tiers[cans] || !cfg.tiers[cans].plans) return null;
    return cfg.tiers[cans].plans[String(interval)] || null;
  }

  // Cadence options to render for a pack size: the tier's plan intervals that are ALSO in the
  // allow-list and not retired, sorted ascending (monthly first). Each = {interval:Number, planId}.
  function cadencesFor(cfg, cans) {
    if (!cfg || !cfg.tiers[cans] || !cfg.tiers[cans].plans) return [];
    var plans = cfg.tiers[cans].plans, out = [];
    for (var k in plans) {
      if (!plans.hasOwnProperty(k)) continue;
      var pid = plans[k];
      if (cfg.sellingPlanIds.length && cfg.sellingPlanIds.indexOf(pid) === -1) continue;
      if (cfg.retiredPlanIds.indexOf(pid) !== -1) continue;
      out.push({ interval: parseInt(k, 10), planId: pid });
    }
    out.sort(function (a, b) { return a.interval - b.interval; });
    return out;
  }

  // Whole-percent subscribe saving for a tier, FLOORED so copy never overstates (e.g. 20).
  function subscribePct(cfg, cans) {
    var t = tier(cfg, cans);
    if (!t || t.subscribeCents == null || !t.onetimeCents) return null;
    return Math.floor(100 * (1 - (t.subscribeCents / t.onetimeCents)));
  }

  function isRetiredPlan(cfg, planId) {
    return !!cfg && cfg.retiredPlanIds.indexOf(normalizePlanId(planId)) !== -1;
  }

  return {
    PACK_STEPS: PACK_STEPS,
    stepsFor: stepsFor,
    parseConfig: parseConfig,
    tier: tier,
    tierTotalCents: tierTotalCents,
    perPouchCents: perPouchCents,
    savingsCents: savingsCents,
    decompose: decompose,
    quantityTotalCents: quantityTotalCents,
    presetMix: presetMix,
    validateMix: validateMix,
    lineItems: lineItems,
    isAllowedPlan: isAllowedPlan,
    allowedPlans: allowedPlans,
    planFor: planFor,
    cadencesFor: cadencesFor,
    subscribePct: subscribePct,
    isRetiredPlan: isRetiredPlan,
    normalizePlanId: normalizePlanId
  };
});
